import React, { useMemo, useState, useCallback } from "react"
import { Link } from "react-router-dom"
import * as Yup from "yup"
import { useFormik } from "formik"
import { toast } from "react-toastify"
import {
  Col,
  Row,
  UncontrolledTooltip,
  Modal,
  ModalHeader,
  ModalBody,
  Form,
  Input,
  FormFeedback,
  Label,
  Card,
  CardBody,
  Button,
} from "reactstrap"

// Custom Components
import TableContainer from "../../../components/Common/TableContainer"
import Breadcrumbs from "../../../components/Common/Breadcrumb"
import DeleteModal from "../../../components/Common/DeleteModal"
import GlobalInputSearch from "components/Common/GlobalInputSearch"

// Custom Hooks
import {
  useCreateLocation,
  useDeleteLocation,
  useEditLocation,
  useLocations,
} from "hooks/locationHook"
import { useListParams } from "hooks/useListParams"

// Utils
import { formatReadableDate } from "utils/formatDate"

const LocationList = () => {
  document.title = "Location List"

  // State Management
  const [modal, setModal] = useState(false)
  const [isEdit, setIsEdit] = useState(false)
  const [location, setLocation] = useState(null)
  const [deleteModal, setDeleteModal] = useState(false)

  // Custom hook for managing URL search parameters
  const { params, setPage, setSearch, resetParams, hasActiveFilters } =
    useListParams({
      page: 1,
      search: "",
    })

  // Memoized query parameters for the API call.
  // Depends on primitive values from `params` for stability.
  const queryParams = useMemo(
    () => ({
      page: params.page,
      per_page: 10, // A more reasonable default per_page value
      ...(params.search && { search: params.search }),
      ...(params.role && { role: params.role }),
      ...(params.department && { department: params.department }),
    }),
    [params.page, params.search, params.role, params.department]
  )

  // React Query hooks for data fetching and mutations
  const {
    data: locationsResponse,
    isLoading,
    refetch: locationRefetch,
    isRefetching: locationRefetchLoading,
  } = useLocations(queryParams)
  const { mutateAsync: createLocation, isPending: locationCreateLoading } =
    useCreateLocation()
  const { mutateAsync: editLocation, isPending: locationEditLoading } = useEditLocation()
  const { mutateAsync: deleteLocation, isPending: locationDeleteLoading } =
    useDeleteLocation()

  // --- Stable Event Handlers using useCallback ---

  const toggle = useCallback(() => {
    if (modal) {
      setModal(false)
      setLocation(null)
      setIsEdit(false)
    } else {
      setModal(true)
    }
  }, [modal])

  const handleAddNewClick = useCallback(() => {
    setLocation(null)
    setIsEdit(false)
    toggle()
  }, [toggle])

  const handleLocationClick = useCallback(
    arg => {
      setLocation({ id: arg.id, name: arg.name })
      setIsEdit(true)
      toggle()
    },
    [toggle]
  )

  const onClickDelete = useCallback(locationData => {
    setLocation(locationData)
    setDeleteModal(true)
  }, [])

  const handleDeleteLocation = useCallback(async () => {
    if (location?.id) {
      try {
        const res = await deleteLocation(location.id)
        toast.success(res?.message ?? "Location deleted successfully.")
        setDeleteModal(false)
      } catch (error) {
        console.error("Failed to delete location:", error)
        toast.error("Failed to delete location.")
      }
    }
  }, [location, deleteLocation])

  const handleLocationListRefresh = () => {
    locationRefetch()
  }

  // Form validation and submission logic
  const validationSchema = Yup.object({
    name: Yup.string()
      .trim()
      .min(2, "Name must be at least 2 characters long")
      .required("Please enter location name"),
  })

  const onSubmit = useCallback(
    async (values, { resetForm }) => {
      const payload = { name: values.name }
      const action = isEdit
        ? editLocation({ id: location.id, ...payload })
        : createLocation(payload)

      try {
        const res = await action
        toast.success(
          res?.message ??
            `Location ${isEdit ? "updated" : "created"} successfully.`
        )
        resetForm()
        toggle()
      } catch (error) {
        console.error("Failed to save location:", error)
        toast.error("Failed to save location.")
      }
    },
    [isEdit, location, editLocation, createLocation, toggle]
  )

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: { name: location?.name || "" },
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit,
  })

  // Table column definition, memoized with stable handlers as dependencies
  const columns = useMemo(
    () => [
      {
        header: "ID",
        accessorKey: "id",
        cell: ({ row }) => (
          <Link to="#" className="text-body fw-bold">
            {row.original.id}
          </Link>
        ),
      },
      {
        header: "Name",
        accessorKey: "name",
      },
      {
        header: "Slug",
        accessorKey: "slug",
      },
      {
        header: "Created",
        accessorKey: "created_at",
        cell: ({ row }) =>
          row.original.created_at
            ? formatReadableDate(row.original.created_at)
            : "-",
      },
      {
        header: "Action",
        cell: ({ row }) => (
          <ul className="list-unstyled hstack gap-1 mb-0">
            <li>
              <Link
                to={`/stock-list?location=${row.original.slug ?? ""}`}
                className="btn btn-sm btn-soft-primary"
                id={`viewtooltip-${row.original.id}`}
              >
                <i className="mdi mdi-eye-outline" />
                <UncontrolledTooltip
                  placement="top"
                  target={`viewtooltip-${row.original.id}`}
                >
                  View
                </UncontrolledTooltip>
              </Link>
            </li>
            <li>
              <button
                type="button"
                className="btn btn-sm btn-soft-info"
                onClick={() => handleLocationClick(row.original)}
                id={`edittooltip-${row.original.id}`}
              >
                <i className="mdi mdi-pencil-outline" />
                <UncontrolledTooltip
                  placement="top"
                  target={`edittooltip-${row.original.id}`}
                >
                  Edit
                </UncontrolledTooltip>
              </button>
            </li>
            <li>
              <button
                type="button"
                className="btn btn-sm btn-soft-danger"
                onClick={() => onClickDelete(row.original)}
                id={`deletetooltip-${row.original.id}`}
              >
                <i className="mdi mdi-delete-outline" />
                <UncontrolledTooltip
                  placement="top"
                  target={`deletetooltip-${row.original.id}`}
                >
                  Delete
                </UncontrolledTooltip>
              </button>
            </li>
          </ul>
        ),
      },
    ],
    [handleLocationClick, onClickDelete]
  )

  const locationsData = locationsResponse?.data?.data || []
  const paginationInfo = locationsResponse?.data

  return (
    <React.Fragment>
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDeleteLocation}
        loading={locationDeleteLoading}
        onCloseClick={() => setDeleteModal(false)}
      />
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="Location" breadcrumbItem="Location Lists" />
          <Row>
            <Col lg="12">
              <Card>
                <CardBody className="border-bottom">
                  <div className="d-flex align-items-center">
                    <h5 className="mb-0 card-title flex-grow-1">Location Lists</h5>
                    <div className="flex-shrink-0">
                      <Button
                        color="primary"
                        onClick={handleAddNewClick}
                        className="me-1"
                      >
                        Add New Location
                      </Button>
                      <Button
                        color="light"
                        onClick={handleLocationListRefresh}
                        className="me-1"
                        id="refreshTooltip"
                      >
                        <i
                          className={`mdi mdi-refresh ${
                            locationRefetchLoading ? "mdi-spin" : ""
                          }`}
                        />
                        <UncontrolledTooltip
                          placement="top"
                          target="refreshTooltip"
                        >
                          Refresh
                        </UncontrolledTooltip>
                      </Button>
                      {hasActiveFilters && (
                        <Button
                          color="danger"
                          onClick={resetParams}
                          className="me-1"
                          id="resetTooltip"
                        >
                          <i className="mdi mdi-close" />
                          <UncontrolledTooltip
                            placement="top"
                            target="resetTooltip"
                          >
                            Reset Filters
                          </UncontrolledTooltip>
                        </Button>
                      )}
                    </div>
                  </div>
                </CardBody>
                <CardBody>
                  <Row>
                    <Col sm={4}>
                      <GlobalInputSearch
                        value={params.search}
                        onChange={setSearch}
                      />
                    </Col>
                  </Row>
                  <TableContainer
                    columns={columns}
                    data={locationsData}
                    isPagination={true}
                    paginationInfo={paginationInfo}
                    onPageChange={setPage}
                    loading={isLoading || locationRefetchLoading}
                    tableClass="align-middle table-nowrap dt-responsive nowrap w-100 table-check dataTable no-footer dtr-inline mt-4 border-top"
                    pagination="pagination"
                    paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
          <Modal isOpen={modal} toggle={toggle} centered>
            <ModalHeader toggle={toggle} tag="h4">
              {isEdit ? "Edit Location" : "Add Location"}
            </ModalHeader>
            <ModalBody>
              <Form
                onSubmit={e => {
                  e.preventDefault()
                  validation.handleSubmit()
                }}
              >
                <div className="mb-3">
                  <Label htmlFor="name">Location Name</Label>
                  <Input
                    name="name"
                    id="name"
                    type="text"
                    placeholder="Enter location name"
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.name || ""}
                    invalid={
                      validation.touched.name && !!validation.errors.name
                    }
                  />
                  {validation.touched.name && validation.errors.name ? (
                    <FormFeedback type="invalid">
                      {validation.errors.name}
                    </FormFeedback>
                  ) : null}
                </div>
                <div className="text-end">
                  <Button
                    color="success"
                    type="submit"
                    disabled={locationEditLoading || locationCreateLoading}
                  >
                    {isEdit ? "Save Changes" : "Add Location"}
                    {(locationEditLoading || locationCreateLoading) && "..."}
                  </Button>
                </div>
              </Form>
            </ModalBody>
          </Modal>
        </div>
      </div>
    </React.Fragment>
  )
}

export default LocationList
